import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCheck, FiChevronDown, FiArrowRight, FiInfo, FiLayers, 
  FiShield, FiDatabase, FiActivity, FiZap 
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { pageFaqs } from '../data/faqs';

const AgencyPlans = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Agency White-Label Plans | ManagedSite.net';
    const description =
      'White-label WordPress care for agencies. Essential, Managed, and Advanced plans starting at $59 a month. You keep the client relationship.';
    document.title = title;
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  const [openFaq, setOpenFaq] = useState(null);

  const plans = [
    {
      name: "Essential Care",
      priceLabel: "Starting at $59/mo",
      description: "Fundamental white-label maintenance and security for standard business websites.",
      features: [
        "WordPress core updates",
        "Plugin and theme updates",
        "Vulnerability monitoring",
        "Offsite backups (S3)",
        "Uptime monitoring",
        "SSL monitoring",
        "Domain/DNS monitoring",
        "Monthly white-label report"
      ],
      cta: "Request Partner Pricing",
      popular: false
    },
    {
      name: "Managed Care",
      priceLabel: "Per site",
      description: "Priority management including technical support time for active business websites.",
      features: [
        "Everything in Essential Care",
        "Daily backup frequency",
        "30 minutes included support/mo",
        "Priority triage",
        "Security hardening",
        "Transactional email monitoring",
        "Performance troubleshooting",
        "Detailed white-label report"
      ],
      cta: "Request Partner Pricing",
      popular: true
    },
    {
      name: "Advanced Care",
      priceLabel: "Per site",
      description: "Advanced management for WooCommerce, membership, and complex integrated websites.",
      features: [
        "Everything in Managed Care",
        "Real-time backup frequency",
        "60 minutes included support/mo",
        "WooCommerce monitoring",
        "Checkout & form testing",
        "Staging site management",
        "Advanced security filtering",
        "Strategic technical review"
      ],
      cta: "Request Partner Pricing",
      popular: false
    }
  ];

  const faqs = pageFaqs['/agency-plans'];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="bg-[#FAF8F3] pt-24 pb-20 lg:pt-36 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Agency Services</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10 max-w-4xl mx-auto">
            White-Label Plans Built for Agencies
          </h1>
          <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-6 max-w-2xl mx-auto font-medium">
            Dependable website management, security, and technical support delivered behind your brand.
          </p>
          <p className="text-2xl sm:text-3xl font-black text-[#174C3C] mb-6">
            Starting at $59 per site / month
          </p>
          <p className="text-lg text-[#17211E]/65 leading-relaxed max-w-2xl mx-auto font-medium mb-12">
            We list a starting price, not the full rate card. Partner rates stay private so you can set your own client pricing and protect your margin.
          </p>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {plans.map((plan, i) => (
              <div key={i} className={`relative flex flex-col p-10 rounded-[2.5rem] border ${plan.popular ? 'border-[#174C3C] shadow-2xl scale-105 z-10' : 'border-[#EAF1EC] shadow-sm'}`}>
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#174C3C] text-white px-6 py-2 rounded-full text-sm font-bold uppercase tracking-widest">
                    Most Popular
                  </div>
                )}
                <div className="mb-10">
                  <h3 className="text-2xl font-extrabold text-[#17211E] mb-4">{plan.name}</h3>
                  <div className="mb-2">
                    <div className="text-2xl font-black text-[#174C3C]">{plan.priceLabel}</div>
                    <div className="text-[#3B7C6D] font-extrabold text-sm mt-2">
                      Volume rate available at 10+ sites
                    </div>
                  </div>
                  <p className="text-lg text-[#17211E]/70 font-medium leading-relaxed mt-6">
                    {plan.description}
                  </p>
                </div>
                <div className="flex-grow mb-10">
                  <ul className="space-y-4">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-start text-lg font-medium text-[#17211E]/80">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-[#174C3C] mr-4 mt-1 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link to="/contact" className={`w-full ${plan.popular ? 'home-button' : 'home-button-secondary'}`}>
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-14 max-w-3xl mx-auto text-center text-lg text-[#17211E]/65 font-medium leading-relaxed">
            Essential Care starts at $59 per site each month. Higher plans and volume rates are shared after a short review — not published publicly — so your agency can charge what the work is worth.
          </p>
        </div>
      </section>

      {/* Volume Pricing */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-[3rem] p-12 md:p-20 border border-[#EAF1EC] shadow-sm">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-[#17211E] mb-8 text-center">Volume Pricing for 10+ Websites</h2>

              <div className="bg-[#EAF1EC] p-10 rounded-2xl border border-[#3B7C6D]/10 space-y-6">
                <p className="text-xl text-[#17211E]/80 leading-relaxed font-medium">
                  Partner rates are per site and depend on the plan. Once an agency reaches 10 active eligible websites, a volume rate applies to all eligible websites beginning with the next billing cycle. Websites must remain active to count toward the portfolio threshold. We send the rate card after a short portfolio review.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-6 border-t border-[#3B7C6D]/10">
                  <div className="text-lg font-black text-[#174C3C]">
                    Essential, Managed, and Advanced Care
                  </div>
                  <Link to="/contact" className="home-button">
                    Request Partner Pricing
                  </Link>
                </div>
              </div>
              
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {['Centralized Billing', 'Dedicated Triage', 'Direct Slack/Teams Access', 'Client-Ready Reports'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-[#FAF8F3] px-5 py-3 rounded-xl border border-[#EAF1EC] font-bold text-[#174C3C]">
                    <SafeIcon icon={FiCheck} />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Blocks */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#17211E] mb-8">On-Demand Technical Support Blocks</h2>
              <p className="text-xl text-[#17211E]/75 mb-10 font-medium leading-relaxed">
                Need technical help beyond routine maintenance? Agencies can purchase prepaid support blocks for troubleshooting, website edits, and project work. Hourly, block, and emergency rates are on the partner rate card.
              </p>
              <div className="space-y-6 mb-12">
                <div className="bg-[#FAF8F3] p-6 rounded-2xl border border-[#EAF1EC] flex justify-between items-center gap-6">
                  <span className="text-xl font-bold text-[#17211E]">5-Hour Support Block</span>
                  <span className="text-sm font-bold uppercase tracking-widest text-[#174C3C]">Prepaid</span>
                </div>
                <div className="bg-[#FAF8F3] p-6 rounded-2xl border border-[#EAF1EC] flex justify-between items-center gap-6">
                  <span className="text-xl font-bold text-[#17211E]">10-Hour Support Block</span>
                  <span className="text-sm font-bold uppercase tracking-widest text-[#174C3C]">Prepaid</span>
                </div>
              </div>
              <div className="bg-[#EAF1EC] p-8 rounded-2xl border border-[#3B7C6D]/10 mb-10">
                <p className="text-xl font-extrabold text-[#17211E] mb-4">Additional technical support is billed hourly.</p>
                <p className="text-lg font-bold text-[#17211E]/80">Emergency or after-hours work, when available, uses a separate emergency rate.</p>
                <p className="mt-4 text-sm font-bold text-[#17211E]/50 italic">Emergency or after-hours availability is not guaranteed.</p>
              </div>
              <Link to="/agency-services/white-label-support" className="text-[#174C3C] font-extrabold hover:text-[#3B7C6D] transition-colors text-xl inline-flex items-center gap-3">
                Learn about White-Label Support <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
            <div className="bg-[#174C3C] rounded-[3rem] p-12 text-white relative overflow-hidden">
              <div className="relative z-10">
                <SafeIcon icon={FiZap} className="w-16 h-16 text-[#D7B77A] mb-8" />
                <h3 className="text-3xl font-bold mb-6">Priority Agency Triage</h3>
                <p className="text-xl text-white/80 leading-relaxed mb-10">
                  Agencies on managed plans receive priority handling for all technical requests. Our team works to resolve critical issues quickly to protect your client relationships.
                </p>
                <div className="bg-white/10 p-8 rounded-2xl border border-white/10 backdrop-blur-sm">
                  <div className="text-sm font-bold uppercase tracking-widest text-[#D7B77A] mb-4">Support Policies</div>
                  <ul className="space-y-4 font-bold">
                    <li className="flex justify-between"><span>Prepaid Block Validity</span> <span>12 Months</span></li>
                    <li className="flex justify-between"><span>Critical Response</span> <span>2-4 Hours</span></li>
                    <li className="flex justify-between"><span>Standard Requests</span> <span>Same/Next Day</span></li>
                  </ul>
                </div>
              </div>
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-white/5 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Plan FAQ</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-[#EAF1EC] rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  className="w-full flex justify-between items-center p-8 text-left hover:bg-[#FAF8F3] transition-colors"
                >
                  <span className="text-xl font-bold text-[#17211E]">{faq.q}</span>
                  <SafeIcon icon={FiChevronDown} className={`w-6 h-6 text-[#3B7C6D] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div className={`px-8 pb-8 text-xl text-[#17211E]/75 font-medium leading-relaxed ${openFaq === i ? 'block' : 'hidden'}`}>
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-10 tracking-tight leading-tight">
            Ready to Offload Your Website Management?
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed">
            Start with one website or discuss a volume plan for your entire agency portfolio.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="home-button-on-dark">
              Request Agency Review
            </Link>
            <Link to="/contact" className="home-button-on-dark-secondary">
              Discuss Agency Partnership
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AgencyPlans;